import { cva } from "class-variance-authority";
import * as React from "react";

import {
  CONTROL_SIZE_TOKENS,
  type ControlSizeProp,
} from "@/components/ui/control-size";
import { cn } from "@/lib/utils";

/**
 * Badge has two independent axes:
 *
 *   `color` — WHICH hue (brand accent, semantic, or raw palette)
 *   `tone`  — HOW it is painted (solid fill / soft tint / outline only)
 *
 * --badge-border-outline	color del borde del tono outline
 * --badge-border-soft	color del borde del tono soft (intensidad 200)
 * --badge-bg-soft	fondo claro/tenue, el del tono soft
 * --badge-text-soft	color del texto sobre ese fondo claro
 * --badge-bg-solid	fondo saturado, el del tono solid
 * --badge-text-solid	color del texto sobre ese fondo saturado
 *
 * They compose without a combinatorial matrix: `color` only sets CSS custom
 * properties, and `tone` is the single consumer of those properties. Adding a
 * colour is one line; adding a tone is one line.
 */
const badgeVariants = cva(
  `inline-flex
  items-center
  justify-center

  transition-colors
  focus:outline-none
  focus:ring-2
  focus:ring-ring
  focus:ring-offset-2`,
  {
    variants: {
      size: {
        // Heights come from the shared control scale so a Badge lines up with
        // a Button or Input of the same `size` placed beside it.
        default: `${CONTROL_SIZE_TOKENS.md.height} px-2.5 py-0.5`,

        "2xs": `${CONTROL_SIZE_TOKENS["2xs"].height} px-[3px] md:px-[4px] py-0 text-[9px] md:text-[10px] leading-none font-light`,
        xs: `${CONTROL_SIZE_TOKENS.xs.height} px-[3px] md:px-[4px] py-[0.4px] md:py-[1.2px] text-xs font-light`,
        sm: `${CONTROL_SIZE_TOKENS.sm.height} px-[5px] md:px-[7px] py-[0.7px] md:py-[1.7px] text-xs`,
        md: `${CONTROL_SIZE_TOKENS.md.height} px-[8px] md:px-[10px] py-[2px] md:py-[3px] text-sm`,
        lg: `${CONTROL_SIZE_TOKENS.lg.height} px-[10px] md:px-[13px] py-[3.5px] md:py-[4px] text-base`,
        xl: `${CONTROL_SIZE_TOKENS.xl.height} px-[11px] md:px-[14px] py-[4.5px] md:py-[5px] text-lg`,
      },
      shape: {
        pill: "rounded-full",
        square: "rounded-md",
      },

      // Reads the custom properties set by `color`. Every tone paints all three
      // of border/background/text, so nothing is ever inherited by accident.
      tone: {
        solid: "bg-[var(--badge-bg-solid)] text-[var(--badge-text-solid)]",
        soft: "border border-[var(--badge-border-soft)] bg-[var(--badge-bg-soft)] text-[var(--badge-text-soft)]",
        outline:
          "border  border-[var(--badge-border-outline)] bg-transparent text-[var(--badge-text-soft)]",
      },

      // Each colour only declares its palette; it never picks a treatment.
      color: {
        neutral:
          "[--badge-border-outline:var(--background7)] [--badge-border-soft:var(--background6)] [--badge-bg-soft:var(--background2)] [--badge-text-soft:var(--foreground)] [--badge-bg-solid:var(--background5)] [--badge-text-solid:var(--foreground)]",
        default:
          "[--badge-border-outline:var(--accent5)] [--badge-border-soft:var(--accent2)] [--badge-bg-soft:var(--accent1)] [--badge-text-soft:var(--accent6)] [--badge-bg-solid:var(--accent2)] [--badge-text-solid:var(--accent6)]",
        secondary:
          "[--badge-border-outline:var(--secondary)] [--badge-border-soft:var(--secondary)] [--badge-bg-soft:var(--secondary-to-white)] [--badge-text-soft:var(--secondary-foreground)] [--badge-bg-solid:var(--secondary)] [--badge-text-solid:var(--secondary-foreground)]",
        success:
          "[--badge-border-outline:var(--success3)] [--badge-border-soft:var(--success2)] [--badge-bg-soft:var(--success1)] [--badge-text-soft:var(--success8)] [--badge-bg-solid:var(--success3)] [--badge-text-solid:var(--success8)]",
        night:
          "[--badge-border-outline:theme(colors.slate.500)] [--badge-border-soft:theme(colors.slate.200)] [--badge-bg-soft:theme(colors.slate.500)] [--badge-text-soft:theme(colors.slate.200)] [--badge-bg-solid:theme(colors.slate.500)] [--badge-text-solid:theme(colors.white)]",
        destructive:
          "[--badge-border-outline:theme(colors.red.500)] [--badge-border-soft:theme(colors.red.200)] [--badge-bg-soft:theme(colors.red.100)] [--badge-text-soft:theme(colors.red.500)] [--badge-bg-solid:theme(colors.red.500)] [--badge-text-solid:theme(colors.white)]",
        warning:
          "[--badge-border-outline:theme(colors.yellow.500)] [--badge-border-soft:theme(colors.yellow.200)] [--badge-bg-soft:theme(colors.yellow.50)] [--badge-text-soft:theme(colors.yellow.700)] [--badge-bg-solid:theme(colors.yellow.500)] [--badge-text-solid:theme(colors.white)]",
        disabled:
          "[--badge-border-outline:theme(colors.gray.300)] [--badge-border-soft:theme(colors.gray.200)] [--badge-bg-soft:theme(colors.gray.100)] [--badge-text-soft:theme(colors.gray.400)] [--badge-bg-solid:theme(colors.gray.300)] [--badge-text-solid:theme(colors.white)]",
        gray: "[--badge-border-outline:theme(colors.gray.500)] [--badge-border-soft:theme(colors.gray.200)] [--badge-bg-soft:theme(colors.gray.200)] [--badge-text-soft:theme(colors.gray.500)] [--badge-bg-solid:theme(colors.gray.500)] [--badge-text-solid:theme(colors.white)]",
        slate:
          "[--badge-border-outline:theme(colors.slate.500)] [--badge-border-soft:theme(colors.slate.200)] [--badge-bg-soft:theme(colors.slate.200)] [--badge-text-soft:theme(colors.slate.500)] [--badge-bg-solid:theme(colors.slate.500)] [--badge-text-solid:theme(colors.white)]",
        blue: "[--badge-border-outline:theme(colors.blue.500)] [--badge-border-soft:theme(colors.blue.200)] [--badge-bg-soft:theme(colors.blue.100)] [--badge-text-soft:theme(colors.blue.500)] [--badge-bg-solid:theme(colors.blue.500)] [--badge-text-solid:theme(colors.white)]",
        green:
          "[--badge-border-outline:theme(colors.green.500)] [--badge-border-soft:theme(colors.green.200)] [--badge-bg-soft:theme(colors.green.100)] [--badge-text-soft:theme(colors.green.500)] [--badge-bg-solid:theme(colors.green.500)] [--badge-text-solid:theme(colors.white)]",
        purple:
          "[--badge-border-outline:theme(colors.purple.500)] [--badge-border-soft:theme(colors.purple.200)] [--badge-bg-soft:theme(colors.purple.100)] [--badge-text-soft:theme(colors.purple.500)] [--badge-bg-solid:theme(colors.purple.500)] [--badge-text-solid:theme(colors.white)]",
        orange:
          "[--badge-border-outline:theme(colors.orange.500)] [--badge-border-soft:theme(colors.orange.200)] [--badge-bg-soft:theme(colors.orange.100)] [--badge-text-soft:theme(colors.orange.500)] [--badge-bg-solid:theme(colors.orange.500)] [--badge-text-solid:theme(colors.white)]",
        amber:
          "[--badge-border-outline:theme(colors.amber.500)] [--badge-border-soft:theme(colors.amber.200)] [--badge-bg-soft:theme(colors.amber.50)] [--badge-text-soft:theme(colors.amber.600)] [--badge-bg-solid:theme(colors.amber.500)] [--badge-text-solid:theme(colors.amber.100)]",
        yellow:
          "[--badge-border-outline:theme(colors.yellow.500)] [--badge-border-soft:theme(colors.yellow.200)] [--badge-bg-soft:theme(colors.yellow.100)] [--badge-text-soft:theme(colors.yellow.500)] [--badge-bg-solid:theme(colors.yellow.500)] [--badge-text-solid:theme(colors.white)]",
      },
    },
    // A pill is never narrower than it is tall, so a one-digit counter renders
    // as a circle instead of an oval. Literal classes so Tailwind can see them.
    compoundVariants: [
      { shape: "pill", size: "default", class: "min-w-7 md:min-w-8" },
      { shape: "pill", size: "2xs", class: "min-w-3.5 md:min-w-4" },
      { shape: "pill", size: "xs", class: "min-w-5 md:min-w-6" },
      { shape: "pill", size: "sm", class: "min-w-6 md:min-w-7" },
      { shape: "pill", size: "md", class: "min-w-7 md:min-w-8" },
      { shape: "pill", size: "lg", class: "min-w-8 md:min-w-9" },
      { shape: "pill", size: "xl", class: "min-w-9 md:min-w-10" },
    ],
    defaultVariants: {
      tone: "solid",
      color: "default",
      size: "sm",
      shape: "pill",
    },
  },
);

export type BadgeTone = "solid" | "soft" | "outline";
export type BadgeColor =
  | "neutral"
  | "default"
  | "secondary"
  | "success"
  | "night"
  | "destructive"
  | "warning"
  | "disabled"
  | "gray"
  | "blue"
  | "green"
  | "purple"
  | "orange"
  | "amber"
  | "yellow"
  | "slate";

export interface BadgeProps extends React.HTMLAttributes<HTMLDivElement> {
  size?: ControlSizeProp | "2xs";
  shape?: "pill" | "square";
  tone?: BadgeTone;
  color?: BadgeColor;
}

const Badge = React.forwardRef<HTMLSpanElement, BadgeProps>(
  ({ className, tone, color, size = "sm", shape, ...props }, ref) => (
    <span
      ref={ref}
      className={cn(badgeVariants({ tone, color, size, shape }), className)}
      {...props}
    />
  ),
);
Badge.displayName = "Badge";

export { Badge, badgeVariants };
